import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Play, Square, Plus, Trash2, X, Camera, Receipt, FileText, MapPin, DollarSign, Image as ImageIcon } from "lucide-react";
import api, { formatErr, fileUrl } from "../lib/apiClient";
import { fmtCAD, fmtDate, statusClass } from "../lib/format";
import CustomerPicker from "../components/CustomerPicker";
import ConfirmDialog from "../components/ConfirmDialog";

const STATUS = ["Scheduled", "On My Way", "In Progress", "Completed", "Cancelled"];
const STATUS_BADGE = {
  Scheduled: "bg-blue-100 text-blue-800",
  "On My Way": "bg-indigo-100 text-indigo-800",
  "In Progress": "bg-amber-100 text-amber-900",
  Completed: "bg-emerald-100 text-emerald-800",
  Cancelled: "bg-slate-100 text-slate-700",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-CA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function hoursFrom(a, b) {
  if (!a || !b) return 0;
  return Math.max((new Date(b).getTime() - new Date(a).getTime()) / 3600000, 0);
}

export function NewJob() {
  // Round-2 regression fix (Feb 2026):
  // - Bug 1 root cause: `NewJob` used to POST a blank draft on mount. React 18 StrictMode
  //   re-runs effects, so every navigation to /jobs/new spawned TWO jobs. Previous fix
  //   attempted a `useRef` guard but forgot to import `useRef`.
  // - New behaviour: never create a blank draft with no customer. If ?customer_id is
  //   present, create immediately (guarded by useRef so we fire exactly once). Otherwise
  //   render an in-page picker and only POST once the user picks a customer.
  //   A job that has no customer isn't a real job — it's noise.
  const nav = useNavigate();
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [search] = useSearchParams();
  const createdRef = useRef(false);
  const prefillCustomerId = search.get("customer_id") || "";

  const createJob = async (customerId) => {
    if (createdRef.current) return;
    createdRef.current = true;
    setBusy(true);
    try {
      const today = new Date().toISOString();
      const { data: job } = await api.post("/jobs", {
        customer_id: customerId,
        job_type: "",
        service_address: "",
        scheduled_start: today,
        scheduled_end: today,
        status: "Scheduled",
        priority: "medium",
        notes: "",
        materials: [],
        job_inputs: [],
        assigned_employee_ids: [],
      });
      nav(`/jobs/${job.id}?fresh=1`, { replace: true });
    } catch (e) {
      createdRef.current = false; // allow retry on error
      setErr(formatErr(e));
      setBusy(false);
    }
  };

  useEffect(() => {
    if (prefillCustomerId) {
      createJob(prefillCustomerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillCustomerId]);

  if (err) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-600 mb-3">{err}</p>
        <Link to="/jobs" className="text-[#0F4C81] hover:underline text-sm">← Back to jobs</Link>
      </div>
    );
  }

  if (prefillCustomerId || busy) {
    return <div className="flex items-center justify-center py-16 text-sm text-slate-500" data-testid="new-job-creating">Creating new job…</div>;
  }

  // No prefill — ask for a customer before creating anything
  return (
    <div className="max-w-xl mx-auto py-8" data-testid="new-job-picker-page">
      <div className="mb-4">
        <Link to="/jobs" className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
          <ArrowLeft size={14}/> Back to jobs
        </Link>
      </div>
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">New job</h1>
        <p className="text-sm text-slate-500 mb-5">Pick a customer to start. You'll fill in job details on the next screen.</p>
        <CustomerPicker
          value=""
          onChange={(id) => id && createJob(id)}
          placeholder="Search by name, address, phone, or email…"
          testid="new-job-customer-picker"
          required
        />
        <div className="mt-4 text-xs text-slate-500">
          Don't see them? <Link to="/customers/new" className="text-[#0F4C81] hover:underline">+ Create a new customer</Link>
        </div>
      </div>
    </div>
  );
}

export function JobsList() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [customers, setCustomers] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [dlg, setDlg] = useState(null);

  const load = async () => {
    const p = new URLSearchParams();
    if (status !== "all") p.set("status", status);
    const [{ data }, { data: cs }] = await Promise.all([api.get(`/jobs?${p}`), api.get("/customers")]);
    setItems(data);
    setCustomers(Object.fromEntries(cs.map((c) => [c.id, c])));
    setSelected(new Set());
  };
  useEffect(() => { load(); }, [status]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };

  const batchCancel = () => {
    if (selected.size === 0) return;
    setDlg({
      title: `Cancel ${selected.size} job${selected.size > 1 ? "s" : ""}?`,
      body: "They remain in history but marked Cancelled.",
      confirmLabel: "Cancel jobs",
      danger: false,
      onConfirm: async () => {
        setBatchBusy(true);
        try {
          const { data } = await api.post("/jobs/batch-cancel", { ids: Array.from(selected) });
          alert(`${data.cancelled} job(s) cancelled.`);
          setSelected(new Set());
          await load();
        } catch (e) { alert(formatErr(e)); }
        finally { setBatchBusy(false); }
      },
    });
  };

  const batchDelete = () => {
    if (selected.size === 0) return;
    setDlg({
      title: `Delete ${selected.size} job${selected.size > 1 ? "s" : ""}?`,
      body: `This will permanently delete the selected ${selected.size} job${selected.size > 1 ? "s" : ""} and their invoices. This cannot be undone.`,
      confirmLabel: "Delete",
      onConfirm: async () => {
        setBatchBusy(true);
        try {
          const { data } = await api.post("/jobs/batch-delete", { ids: Array.from(selected) });
          alert(`${data.deleted} job(s) deleted.`);
          setSelected(new Set());
          await load();
        } catch (e) { alert(formatErr(e)); }
        finally { setBatchBusy(false); }
      },
    });
  };

  const deleteOne = (job) => {
    setDlg({
      title: `Delete job ${job.number}?`,
      body: "This permanently deletes the job and its invoice. This cannot be undone.",
      confirmLabel: "Delete",
      onConfirm: async () => {
        try {
          await api.post("/jobs/batch-delete", { ids: [job.id] });
          await load();
        } catch (e) { alert(formatErr(e)); }
      },
    });
  };

  return (
    <div data-testid="jobs-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Open Jobs</h1><p className="text-sm text-slate-500 mt-1">{items.length} active job{items.length !== 1 ? "s" : ""}{selected.size > 0 && ` · ${selected.size} selected`} · paid &amp; completed jobs live in <Link to="/invoices" className="underline hover:text-slate-700">Invoices</Link></p></div>
        <Link to="/jobs/new" className="self-start px-4 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Plus size={16} /> New Job</Link>
      </div>
      <div className="flex gap-3 flex-wrap items-center">
        <select data-testid="job-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:border-[#0F4C81]">
          <option value="all">All statuses</option>
          {STATUS.map((s) => <option key={s}>{s}</option>)}
        </select>
        {selected.size > 0 && (
          <div data-testid="jobs-batch-bar" className="flex items-center gap-2 px-3 py-1.5 bg-[#E0F2FE] border border-[#0F4C81]/30 rounded-md">
            <span className="text-xs font-medium text-[#0F4C81]">{selected.size} selected</span>
            <button onClick={batchCancel} disabled={batchBusy} data-testid="jobs-batch-cancel-btn" className="px-2.5 py-1 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-50">Cancel</button>
            <button onClick={batchDelete} disabled={batchBusy} data-testid="jobs-batch-delete-btn" className="px-2.5 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded flex items-center gap-1 disabled:opacity-50"><Trash2 size={11}/>Delete</button>
            <button onClick={() => setSelected(new Set())} className="px-2.5 py-1 text-xs border border-slate-300 text-slate-700 rounded hover:bg-white">Clear</button>
          </div>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 w-8"><input type="checkbox" data-testid="jobs-select-all" checked={items.length > 0 && selected.size === items.length} onChange={toggleAll}/></th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Job #</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Customer</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Type</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Scheduled</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Priority</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500 font-medium">Status</th>
                <th className="px-2 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No jobs yet.</td></tr>}
              {items.map((j) => (
                <tr key={j.id} data-testid={`job-row-${j.id}`} className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${selected.has(j.id) ? "bg-blue-50/40" : ""}`} onClick={() => window.location.href = `/jobs/${j.id}`}>
                  <td className="px-3 py-3" onClick={(e) => { e.stopPropagation(); toggle(j.id); }}>
                    <input type="checkbox" data-testid={`job-select-${j.id}`} checked={selected.has(j.id)} onChange={() => toggle(j.id)}/>
                  </td>
                  <td className="px-4 py-3 font-mono font-medium">{j.number}</td>
                  <td className="px-4 py-3">{customers[j.customer_id]?.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{j.job_type || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDateTime(j.scheduled_start)}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${j.priority === "high" ? "bg-red-100 text-red-800" : j.priority === "low" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"} capitalize`}>{j.priority}</span></td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${STATUS_BADGE[j.status] || ""}`}>{j.status}</span></td>
                  <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); deleteOne(j); }}
                      data-testid={`job-row-delete-${j.id}`}
                      title="Delete job"
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                    ><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmDialog dialog={dlg} onClose={() => setDlg(null)} />
    </div>
  );
}

export { default as JobDetail } from "./jobs/JobDetail";
